import type { Query } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import type { NoteDTO } from "../../../api/api";
import type {
  AddInvoiceRequest,
  CostDetailsResponse,
  CostInvoiceStatus,
  FileDTO,
  InvoiceResponse,
  UpdateCostRequest,
} from "../costApi";
import {
  addAttachment,
  addInvoice,
  deleteAttachment,
  deleteCost,
  deleteInvoice,
  getCost,
  InvoiceAttachedToManyCostsError,
  updateCost,
  updateCostNotes,
  updateInvoicesExchangeRate,
  updateInvoiceStatus,
} from "../costApi";

const COSTS_QUERY_KEY = "costs";

export function isListCostsQuery({ queryKey }: Query) {
  return queryKey.length > 1 && queryKey[0] === COSTS_QUERY_KEY && typeof queryKey[1] === "object";
}

export interface AddInvoiceRequestStandalone {
  invoiceData: AddInvoiceRequest;
  costId: string;
  bookingNumber?: string;
}

export function useAddInvoiceMutation() {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const mutation = useMutation<InvoiceResponse, unknown, AddInvoiceRequestStandalone>({
    mutationFn: (req) => addInvoice(req.costId, req.invoiceData),
    onSuccess: async (_, req) => {
      enqueueSnackbar(`Faktura została dołączona do kosztu rezerwacji ${req.bookingNumber ?? ""}`, {
        variant: "success",
      });
      await queryClient.invalidateQueries({ queryKey: [COSTS_QUERY_KEY, req.costId] });
      await queryClient.invalidateQueries({ predicate: isListCostsQuery });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się dodać faktury. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });
  return (invoice: AddInvoiceRequestStandalone) => mutation.mutateAsync(invoice);
}
export function useUpdateCostMutation() {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const mutation = useMutation<CostDetailsResponse, unknown, { costId: string; update: UpdateCostRequest }>({
    mutationFn: (req) => updateCost(req.costId, req.update),
    onSuccess: async ({ bookingNumber }) => {
      enqueueSnackbar(`Szczegóły kosztu rezerwacji ${bookingNumber ?? ""} zostały zaktualizowane`, {
        variant: "success",
      });
      await queryClient.invalidateQueries({ predicate: ({ queryKey }) => queryKey[0] === COSTS_QUERY_KEY });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować kosztu rezerwacji. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });
  return (costId: string, update: UpdateCostRequest) => mutation.mutateAsync({ costId, update });
}

export const useCost = () => {
  const { costId: costIdParam } = useParams<{ costId: string }>();
  const costId = costIdParam!;
  const { data, isLoading } = useQuery({
    queryKey: [COSTS_QUERY_KEY, costId],
    queryFn: ({ signal }) => getCost(costId, signal),
  });
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const updateCostMutation = useMutation<CostDetailsResponse, unknown, { update: UpdateCostRequest }>({
    mutationFn: ({ update }) => updateCost(costId, update),
    onSuccess: async ({ bookingNumber }) => {
      enqueueSnackbar(`Szczegóły kosztu rezerwacji ${bookingNumber ?? ""} zostały zaktualizowane`, {
        variant: "success",
      });
      await queryClient.invalidateQueries({ queryKey: [COSTS_QUERY_KEY, costId] });
      await queryClient.invalidateQueries({ predicate: isListCostsQuery });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować kosztu rezerwacji. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const deleteCostMutation = useMutation<void, unknown, void>({
    mutationFn: () => deleteCost(costId),
    onSuccess: async () => {
      enqueueSnackbar(`Szczegóły kosztu rezerwacji ${data?.bookingNumber ?? ""} zostały usunięte`, {
        variant: "success",
      });
      queryClient.removeQueries({ queryKey: [COSTS_QUERY_KEY, costId] });
      await queryClient.invalidateQueries({ predicate: isListCostsQuery });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć kosztu rezerwacji. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const addInvoiceMutation = useMutation<InvoiceResponse, unknown, AddInvoiceRequest>({
    mutationFn: (i) => addInvoice(costId, i),
    onSuccess: async () => {
      enqueueSnackbar(`Faktura została dołączona do kosztu rezerwacji ${data?.bookingNumber ?? ""}`, {
        variant: "success",
      });
      await queryClient.invalidateQueries({ queryKey: [COSTS_QUERY_KEY, costId] });
      await queryClient.invalidateQueries({ predicate: isListCostsQuery });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się dodać faktury. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const updateInvoicesExchangeRateMutation = useMutation<InvoiceResponse[], unknown, void>({
    mutationFn: () => updateInvoicesExchangeRate(costId),
    onSuccess: async () => {
      enqueueSnackbar(`Kursy wymiany walut dla faktur zostały zaktualizowane`, { variant: "success" });
      queryClient.removeQueries({ queryKey: [COSTS_QUERY_KEY, costId] });
    },
    onError: (error) => {
      enqueueSnackbar(
        `Nie udało się zaktualizować kursów wymiany walut dla faktur. Powód: ${(error as Error).message}`,
        {
          variant: "error",
        },
      );
    },
  });

  const deleteInvoiceMutation = useMutation<void, unknown, { invoiceNumber: string; deleteFromAll: boolean }>({
    mutationFn: ({ deleteFromAll, invoiceNumber }) => deleteInvoice(costId, invoiceNumber, deleteFromAll),
    onSuccess: async () => {
      enqueueSnackbar("Faktura została usunięta", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: [COSTS_QUERY_KEY, costId] });
      await queryClient.invalidateQueries({ predicate: isListCostsQuery });
    },
    onError: (error) => {
      if (!(error instanceof InvoiceAttachedToManyCostsError)) {
        enqueueSnackbar(`Nie udało się usunąć faktury. Powód: ${(error as Error).message}`, {
          variant: "error",
        });
      }
    },
  });

  const updateInvoiceStatusMutation = useMutation<void, unknown, { invoiceNumber: string; status: CostInvoiceStatus }>({
    mutationFn: ({ status, invoiceNumber }) => updateInvoiceStatus(costId, invoiceNumber, status),
    onSuccess: async () => {
      enqueueSnackbar("Status faktury został zaktualizowany", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: [COSTS_QUERY_KEY, costId] });
      await queryClient.invalidateQueries({ predicate: isListCostsQuery });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować statusu faktury. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const addAttachmentMutation = useMutation<FileDTO, unknown, { body: FormData }>({
    mutationFn: ({ body }) => addAttachment(costId, body),
    onSuccess: async (r) => {
      enqueueSnackbar(`Załącznik ${r.name} został dołączony do kosztu`, {
        variant: "success",
      });
      await queryClient.invalidateQueries({ queryKey: [COSTS_QUERY_KEY, costId] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się dodać załącznika. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const deleteAttachmentMutation = useMutation<FileDTO[], unknown, { name: string }>({
    mutationFn: ({ name }) => deleteAttachment(costId, name),
    onSuccess: async () => {
      enqueueSnackbar("Załącznik został usunięty", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: [COSTS_QUERY_KEY, costId] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć załącznika. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const updateCostNotesMutation = useMutation<CostDetailsResponse, unknown, { notes: NoteDTO[] }>({
    mutationFn: ({ notes }) => updateCostNotes(costId, notes),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [COSTS_QUERY_KEY, costId] });
    },
  });

  return {
    cost: data,
    isLoading,
    updateCost: (update: UpdateCostRequest) => updateCostMutation.mutateAsync({ update }),
    addInvoice: (invoice: AddInvoiceRequest) => addInvoiceMutation.mutateAsync(invoice),
    updateInvoiceStatus: (invoiceNumber: string, status: CostInvoiceStatus) =>
      updateInvoiceStatusMutation.mutateAsync({
        invoiceNumber,
        status,
      }),
    deleteInvoice: (invoiceNumber: string, deleteFromAll: boolean) =>
      deleteInvoiceMutation.mutateAsync({ invoiceNumber, deleteFromAll }),
    updateInvoicesExchangeRate: () => updateInvoicesExchangeRateMutation.mutateAsync(),
    deleteCost: () => deleteCostMutation.mutateAsync(),
    addAttachment: (body: FormData) => addAttachmentMutation.mutateAsync({ body }),
    deleteAttachment: (name: string) => deleteAttachmentMutation.mutateAsync({ name }),
    updateNotes: (notes: NoteDTO[]) => updateCostNotesMutation.mutateAsync({ notes }),
  };
};
